import {
  ActionIcon,
  Button,
  Card,
  Code,
  Checkbox,
  Group,
  Select,
  Stack,
  TagsInput,
  TextInput,
  Table,
  Title,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { Link } from "wouter";
import { useState } from "react";
import { getProductType } from "../api";

const PREDEFINED_ROLES = [
  "uniqueIdentifier",
  "productGroup",
  "mainImage",
  "category",
  "brand",
  "name",
];

export function AbstractMappingForm({ marketplaceId, initialData, onSubmit }) {
  const [baseCategory, setBaseCategory] = useState("");
  // const [productType, setProductType] = useState(null);

  const initialValues = {
    categoryMapper: initialData?.categoryMapper || "MiraklCategoryPathMapper",
    predefinedMappings: PREDEFINED_ROLES.reduce(
      (acc, role) => ({
        ...acc,
        [role]: initialData?.systemFieldMappings?.[role] || "",
      }),
      {}
    ),
    customMappings:
      initialData?.mappings ||
      [
        // { pattern: "", isCreative: false, neverTouch: false },
      ],
    metaFields:
      initialData?.meta ||
      [
        // { pattern: "", isCreative: false, neverTouch: false },
      ],
  };

  // const initialValues2 = {
  //   categoryMapper: "miraklCategoryPathMapper",
  //   predefinedMappings: { uniqueIdentifier: "EAN" },
  //   customMappings: [],
  //   metaFields: [],
  // };

  const form = useForm({
    initialValues: initialValues,
  });

  const handleSubmit = (values) => {
    onSubmit(values);
  };

  const fetchBaseCategory = () => {
    console.log("fetching base category, baseCategory:", baseCategory);
    getProductType(marketplaceId, baseCategory).then((data) => {
      // setProductType(data.hydratedType);
      // Build a set of all existing tokens (sources + destination) to avoid duplicates
      const used = new Set();
      for (const m of form.values.customMappings) {
        if (Array.isArray(m.sources)) {
          m.sources.filter(Boolean).forEach((s) => used.add(s));
        }
        // Some older entries might have a singular 'source' key
        if (m.source) used.add(m.source);
        if (m.destination) used.add(m.destination);
      }

      const newMappings = data.hydratedType.attributes
        .filter((attr) => {
          const code = attr.code;
          // attr.label might be undefined / null
          const label = attr.label;
          return !used.has(code) && (!label || !used.has(label));
        })
        .map((attr) => ({
          sources: [attr.code, attr.label].filter(Boolean),
          destination: attr.code,
        }));

      form.setValues({
        ...form.values,
        baseCategoryId: baseCategory,
        customMappings: [...form.values.customMappings, ...newMappings],
      });
    });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      {/* <Code block>{JSON.stringify(initialValues2, null, 2)}</Code>
      <Code block>{JSON.stringify(initialValues, null, 2)}</Code> */}

      <Stack spacing="lg">
        <Card withBorder>
          <Title order={3} mb="md">
            Select Base Category
          </Title>
          <Link target="_blank" to="/product-types/tree">
            Browse Types
          </Link>
          <Group align="flex-end">
            <TextInput
              label="Base Category"
              value={baseCategory}
              onChange={(event) => setBaseCategory(event.currentTarget.value)}
            />
            <Button onClick={() => fetchBaseCategory()}>Fetch</Button>
          </Group>
        </Card>

        <Select
          label="Category Mapper"
          {...form.getInputProps("categoryMapper")}
          data={["miraklCategoryPathMapper", "miraklCategoryCodeMapper"]}
        />
        {/* 
        <Card withBorder>
          <Title order={3} mb="md">
            Predefined Mappings
          </Title>
          <Stack>
            {PREDEFINED_ROLES.map((role) => (
              <TextInput
                key={form.key(`predefinedMappings.${role}`)}
                label={role}
                placeholder="Enter regex pattern"
                {...form.getInputProps(`predefinedMappings.${role}`)}
              />
            ))}
          </Stack>
        </Card> */}

        <Card withBorder>
          <Title order={3} mb="md">
            Custom Mappings
          </Title>
          <Table
            stickyHeader
            stickyHeaderOffset={30}
            striped
            highlightOnHover
            withRowBorders={false}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Sources</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Destination</Table.Th>
                <Table.Th>Creative</Table.Th>
                <Table.Th>No Touch</Table.Th>
                <Table.Th>Scored</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {form.values.customMappings.map((_, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <TagsInput
                      {...form.getInputProps(`customMappings.${index}.sources`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={PREDEFINED_ROLES}
                      {...form.getInputProps(`customMappings.${index}.role`)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      placeholder="Enter regex pattern"
                      {...form.getInputProps(
                        `customMappings.${index}.destination`
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.isCreative`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.doNotAskAi`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      {...form.getInputProps(
                        `customMappings.${index}.isScored`,
                        {
                          type: "checkbox",
                        }
                      )}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        form.removeListItem("customMappings", index)
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={() =>
              form.insertListItem("customMappings", {
                source: "",
                destination: "",
                isCreative: false,
                neverTouch: false,
              })
            }
          >
            Add Custom Mapping
          </Button>
        </Card>

        <Card withBorder>
          <Title order={3} mb="md">
            Meta Fields
          </Title>
          <Stack spacing="md">
            {form.values.metaFields.map((_, index) => (
              <Group key={index} align="flex-end">
                <TextInput
                  label="Regex Pattern"
                  placeholder="Enter regex pattern"
                  {...form.getInputProps(`metaFields.${index}.source`)}
                />

                <Checkbox
                  label="Is Creative"
                  {...form.getInputProps(`metaFields.${index}.isCreative`, {
                    type: "checkbox",
                  })}
                />
                <Checkbox
                  label="Never Touch"
                  {...form.getInputProps(`metaFields.${index}.neverTouch`, {
                    type: "checkbox",
                  })}
                />

                <ActionIcon
                  color="red"
                  onClick={() => form.removeListItem("metaFields", index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              onClick={() =>
                form.insertListItem("metaFields", {
                  source: "",
                  isCreative: false,
                  neverTouch: false,
                })
              }
            >
              Add Meta Field
            </Button>
          </Stack>
        </Card>

        <Group position="right">
          <Button type="submit">Save Changes</Button>
        </Group>
      </Stack>
    </form>
  );
}
